import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useOutlet } from './OutletContext';

const ShiftContext = createContext(null);

export function ShiftProvider({ children }) {
  const { api, user, isAuthenticated } = useAuth();
  const { currentOutlet } = useOutlet();
  const [currentShift, setCurrentShift] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCurrentShift = useCallback(async () => {
    if (!api || !currentOutlet?.id || !isAuthenticated) {
      setCurrentShift(null);
      return;
    }

    try {
      const resp = await api.get('/shifts/current', {
        params: { outlet_id: currentOutlet.id }
      });
      setCurrentShift(resp.data);
    } catch (error) {
      console.error('Failed to fetch current shift:', error);
      setCurrentShift(null);
    }
  }, [api, currentOutlet?.id, isAuthenticated]);

  useEffect(() => {
    fetchCurrentShift();
  }, [fetchCurrentShift]);

  const openShift = async (openingCash, notes = '') => {
    if (!currentOutlet?.id) return null;
    
    setLoading(true);
    try {
      const resp = await api.post('/shifts/open', {
        outlet_id: currentOutlet.id,
        opening_cash: parseFloat(openingCash),
        notes: notes || null
      });
      setCurrentShift(resp.data.shift);
      return resp.data.shift;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const closeShift = async (closingCashActual, differenceReason = null) => {
    if (!currentShift?.id) return null;
    
    setLoading(true);
    try {
      const resp = await api.post(`/shifts/${currentShift.id}/close`, {
        closing_cash_actual: parseFloat(closingCashActual),
        difference_reason: differenceReason || null
      });
      setCurrentShift(null);
      return resp.data.shift;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getShiftSummary = async () => {
    if (!currentShift?.id) return null;
    
    try {
      const resp = await api.get(`/shifts/${currentShift.id}/summary`);
      return resp.data;
    } catch (error) {
      console.error('Failed to get shift summary:', error);
      return null;
    }
  };

  const hasActiveShift = !!currentShift;

  return (
    <ShiftContext.Provider value={{
      currentShift,
      hasActiveShift,
      loading,
      openShift,
      closeShift,
      getShiftSummary,
      refreshShift: fetchCurrentShift
    }}>
      {children}
    </ShiftContext.Provider>
  );
}

export function useShift() {
  const context = useContext(ShiftContext);
  if (!context) {
    throw new Error('useShift must be used within a ShiftProvider');
  }
  return context;
}

export default ShiftContext;
